/**
 * Máquina de fase do turno (spec-007 R1) — a pílula do header, derivada dos
 * eventos SSE reais.
 *
 * O backend não emite evento de "fase" (gap aceito na spec §4): a fase nasce
 * da sequência `thinking → painel_evento → token → end` que o turno já produz.
 * Função pura (estado, nomeDoEvento) → estado, para o teste fixar a transição
 * exata do §6.6 sem montar componente nem stream.
 *
 * `vuPainel` é memória do turno: depois que o painel abriu, um `tool_call` ou
 * `token` tardio nunca regride o rótulo para "Elaborando resposta" — o gestor
 * veria a pílula "voltar no tempo" enquanto o parecer está sendo consolidado.
 *
 * Labels em PT-BR são os do protótipo v0 (reusados literalmente).
 */

export const FASE_INICIAL = null

// A fase em que uma tool de servidor está rodando (`tool_call`/`tool_result`
// fora do painel). Exportada porque o rótulo único do turno
// (`utils/rotulo-do-turno.js`) a troca pelo `TOOL_LABELS` da tool em voo — e,
// sem tool em voo, pelo rótulo de raciocínio: entre o resultado e o próximo
// token o modelo está lendo o que a tool devolveu, não "consultando dados".
export const FASE_TOOLS = 'tools'

// Exportados desde 11/09/2026 para o rótulo único do turno reusar o MESMO
// texto — duas grafias do mesmo fato fariam o rodapé dizer uma coisa e a
// máquina outra.
export const FASE_LABELS = {
  reasoning: 'Raciocinando',
  tools: 'Consultando dados',
  agents: 'Consultando agentes',
  answer: 'Consolidando parecer',
  answerDireto: 'Elaborando resposta',
}

const LABELS = FASE_LABELS

/**
 * Devolve a MESMA referência de `estado` quando nada mudou: o consumidor
 * (`_atualizarFase`) usa identidade como guard, e um objeto novo por evento
 * viraria um dispatch Vuex por TOKEN — exatamente o que o buffer por rAF do
 * apiChat existe para evitar.
 */
function fase(estado, phase, label, vuPainel) {
  const vu = Boolean(vuPainel)
  if (
    estado &&
    estado.phase === phase &&
    estado.label === label &&
    estado.vuPainel === vu
  ) {
    return estado
  }
  return { phase, label, vuPainel: vu }
}

export function faseAposEvento(estado, nomeDoEvento) {
  const vuPainel = Boolean(estado && estado.vuPainel)
  switch (nomeDoEvento) {
    case 'start':
      return FASE_INICIAL
    case 'thinking':
      return fase(estado, 'reasoning', LABELS.reasoning, vuPainel)
    case 'painel_evento':
      return fase(estado, 'agents', LABELS.agents, true)
    case 'painel':
      // A transcrição final chega DEPOIS do parecer começar a fluir — ela
      // marca a memória do painel mas nunca regride o rótulo.
      return estado
        ? fase(estado, estado.phase, estado.label, true)
        : fase(estado, 'agents', LABELS.agents, true)
    case 'tool_call':
    case 'tool_result':
      // Tool do próprio painel não apaga a memória do painel.
      return vuPainel
        ? fase(estado, 'agents', LABELS.agents, true)
        : fase(estado, FASE_TOOLS, LABELS.tools, false)
    case 'token':
      return vuPainel
        ? fase(estado, 'answer', LABELS.answer, true)
        : fase(estado, 'answer', LABELS.answerDireto, false)
    case 'end':
    case 'error':
      return FASE_INICIAL
    default:
      // Evento desconhecido não muda a fase — o backend pode estrear eventos
      // antes de o frontend saber deles (mesma regra do apiChat).
      return estado
  }
}
